import { Box, Card, HStack, Stack, Text } from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from '../../../../ui/components/chart/rc-theme'
import { Legend } from 'recharts'
import {
  parseNumber,
  serviceLabel,
  CAPACITY_UNIT_BY_LABEL,
  buildMonthlyProgressData,
  formatProgressTooltipValue,
  formatUnitTooltipValue,
  type MonthlyProgressItem,
} from '../capacity.utils'

type ServiceEntry = {
  name: string
  unit: string
  Bestellt: number
  Durchgeführt: number
}

function buildServiceData(ordered: string[], done: number[]): ServiceEntry[] {
  return [0, 1, 2, 3].map(i => {
    const label = serviceLabel(i)
    return {
      name: label,
      unit: CAPACITY_UNIT_BY_LABEL[label],
      Bestellt: parseNumber(ordered[i] ?? '0'),
      Durchgeführt: done[i] ?? 0,
    }
  })
}

export function CapacityBarChart({
  title,
  ordered,
  done,
}: {
  title?: string
  ordered: string[]
  done: number[]
}) {
  const data = buildServiceData(ordered, done)

  return (
    <Card.Root variant="outline" borderColor="border" bg="bg">
      <Card.Header>
        <Stack gap={0.5}>
          <Text fontWeight="semibold" color="fg">
            Geplant vs. Durchgeführt
          </Text>
          {title && (
            <Text fontSize="sm" color="fg.subtle">
              {title}
            </Text>
          )}
        </Stack>
      </Card.Header>
      <Card.Body>
        <Box w="full" h="220px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="border" />
              <XAxis dataKey="name" style={{ fontSize: '12px' }} stroke="fg.subtle" />
              <YAxis style={{ fontSize: '11px' }} stroke="fg.subtle" width={40} />
              <Tooltip
                formatter={(value: number, name: string, props: any) => [
                  formatUnitTooltipValue(value, props.payload?.unit ?? ''),
                  name,
                ]}
                contentStyle={{
                  background: 'var(--chakra-colors-bg)',
                  border: '1px solid var(--chakra-colors-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                formatter={(value: string) => value}
              />
              <Bar dataKey="Bestellt" fill="blue.muted" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Durchgeführt" fill="green.muted" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}

export function CapacityProgressChart({
  title,
  ordered,
  done,
  monthlyData,
}: {
  title?: string
  ordered: string[]
  done: number[]
  monthlyData?: MonthlyProgressItem[]
}) {
  const data = buildMonthlyProgressData(ordered, done, monthlyData)

  return (
    <Card.Root variant="outline" borderColor="border" bg="bg">
      <Card.Header>
        <Stack gap={0.5}>
          <Text fontWeight="semibold" color="fg">
            Jahresverlauf Durchführung
          </Text>
          {title && (
            <Text fontSize="sm" color="fg.subtle">
              {title}
            </Text>
          )}
        </Stack>
      </Card.Header>
      <Card.Body>
        <Box w="full" h="220px">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="border" />
              <XAxis dataKey="month" style={{ fontSize: '11px' }} stroke="fg.subtle" />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(v: number) => `${v}%`}
                style={{ fontSize: '11px' }}
                stroke="fg.subtle"
                width={40}
              />
              <Tooltip
                formatter={(value, name: string) => [
                  formatProgressTooltipValue(value as number | null),
                  name,
                ]}
                contentStyle={{
                  background: 'var(--chakra-colors-bg)',
                  border: '1px solid var(--chakra-colors-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              <Line
                type="monotone"
                dataKey="Plan"
                stroke="orange.solid"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="Durchgeführt"
                stroke="blue.solid"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}

export function SibCapacityBudgetChart({
  sibName,
  frame,
  ordered,
  done,
}: {
  sibName: string
  frame: string[]
  ordered: string[]
  done: number[]
}) {
  const data: Array<{
    name: string
    unit: string
    Kapazität: number
    Bestellt: number
    Durchgeführt: number
  }> = [0, 1, 2, 3].map(i => {
    const label = serviceLabel(i)
    return {
      name: label,
      unit: CAPACITY_UNIT_BY_LABEL[label],
      Kapazität: parseNumber(frame[i] ?? '0'),
      Bestellt: parseNumber(ordered[i] ?? '0'),
      Durchgeführt: done[i] ?? 0,
    }
  })

  return (
    <Card.Root variant="outline" borderColor="border" bg="bg">
      <Card.Header>
        <Stack gap={0.5}>
          <Text fontWeight="semibold" color="fg">
            Kapazität vs. Bestellung vs. Durchführung
          </Text>
          <Text fontSize="sm" color="fg.subtle">
            {sibName}
          </Text>
        </Stack>
      </Card.Header>
      <Card.Body>
        <Box w="full" h="240px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              barCategoryGap="28%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="border" />
              <XAxis dataKey="name" style={{ fontSize: '12px' }} stroke="fg.subtle" />
              <YAxis style={{ fontSize: '11px' }} stroke="fg.subtle" width={40} />
              <Tooltip
                formatter={(value: number, name: string, props: any) => [
                  formatUnitTooltipValue(value, props.payload?.unit ?? ''),
                  name,
                ]}
                contentStyle={{
                  background: 'var(--chakra-colors-bg)',
                  border: '1px solid var(--chakra-colors-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              <Bar dataKey="Kapazität" fill="gray.muted" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Bestellt" fill="blue.muted" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Durchgeführt" fill="green.muted" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}

export function OverviewSummaryChart({
  rows,
}: {
  rows: Array<{ sib: string; usagePct: number; targetPct: number; revenue: number }>
}) {
  return (
    <Card.Root variant="outline" borderColor="border" bg="bg">
      <Card.Header>
        <Text fontWeight="semibold" color="fg">
          SIB-Auslastung Übersicht
        </Text>
      </Card.Header>
      <Card.Body>
        <Box w="full" h="240px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows.map(r => ({
                name: r.sib.split(' ').at(-1),
                Auslastung: r.usagePct,
                Zeitziel: r.targetPct,
              }))}
              margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="border" />
              <XAxis dataKey="name" style={{ fontSize: '11px' }} stroke="fg.subtle" interval={0} />
              <YAxis
                domain={[0, 120]}
                tickFormatter={(v: number) => `${v}%`}
                style={{ fontSize: '11px' }}
                stroke="fg.subtle"
                width={40}
              />
              <Tooltip
                formatter={(value: number, name: string) => [`${value}%`, name]}
                contentStyle={{
                  background: 'var(--chakra-colors-bg)',
                  border: '1px solid var(--chakra-colors-border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
              <Bar dataKey="Auslastung" fill="blue.muted" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Zeitziel" fill="teal.muted" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <HStack justify="center" gap={6} mt={2} fontSize="xs" color="fg.subtle">
          <Text>Auslastung: Bestellt / Kapazität</Text>
          <Text>Zeitziel: Fortschritt vs. Jahresplan</Text>
        </HStack>
      </Card.Body>
    </Card.Root>
  )
}
